import React, { useState } from 'react'
import Modal from '../common/Modal'
import { useFileUpload } from '../../hooks/useFileUpload'
import { useMutation } from '@tanstack/react-query'
import { handlePostRequest } from '../../utils/api-handler.util'
import { showToaster } from '../../utils/show-toaster.util'
import { useAuth } from '../../contexts/AuthContext'
import { QueryKeys } from '../../utils/queryKeyGenerator'

const UploadShopDocumentModal = ({ isOpen, onClose, documentType }) => {
  const { currentUser } = useAuth()
  const [isUploading, setIsUploading] = useState(false)

  const { renderUploadInput, handleFileUpload, selectedFiles, clearFiles } = useFileUpload({
    label: 'Upload Document',
    dropZoneOptions: {
      required: true,
      accept: {
        'application/pdf': ['.pdf'],
      },
    },
  })

  const uploadDocumentMutation = useMutation({
    mutationFn: async ({ filePath }) => {
      return handlePostRequest(`/${currentUser.userType}s/shop-documents`, {
        filePath,
        documentTypeId: documentType.id,
      })
    },
    onSuccess: () => {
      QueryKeys.shopDocuments.invalidate(currentUser.userType)
      QueryKeys.shopVerificationDetails.invalidate(currentUser.id)
      showToaster('success', 'Document uploaded successfully')
      handleClose()
    },
    onError: (error) => {
      showToaster('error', error?.response?.data?.message || 'Failed to upload document')
    },
  })

  const handleSave = async (e) => {
    e.preventDefault()

    if (selectedFiles.length === 0) {
      showToaster('error', 'Please upload a file')
      return
    }

    setIsUploading(true)
    try {
      const uploadedFileUrl = await handleFileUpload()
      if (uploadedFileUrl) {
        await uploadDocumentMutation.mutateAsync({
          filePath: uploadedFileUrl,
        })
      }
    } catch (error) {
      showToaster('error', 'Failed to upload document')
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    clearFiles()
    onClose()
  }

  if (!documentType) return null

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Upload {documentType.name}</h2>
        {documentType.description && (
          <p className="text-sm text-gray-600">{documentType.description}</p>
        )}

        <form onSubmit={handleSave} className="space-y-6">
          <div>{renderUploadInput()}</div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-neutral-200-100 border border-gray-300 rounded-md hover:bg-neutral-200"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading}
              className="px-4 py-2 text-sm font-medium text-white bg-secondary-600 border border-transparent rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? 'Uploading...' : 'Upload Document'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default UploadShopDocumentModal
